import classNames from "classnames";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import useThemePreference from "@/hooks/useThemePreference";

interface props {
    current: number;
    total: number;
    showGlow?: boolean;
    colorOverride?: string;
}

// Format numbers to 1k, 2k, etc. if they are an exact multiple of 1000
const formatToK = (num: number): string =>
    num % 1000 === 0 && num >= 1000 ? `${num / 1000}k` : num.toString();

function CircularProgressIndicator({current, total, showGlow = false, colorOverride}: props) {
    const percentage = current / total * 100;
    const strLength = total.toString().length + current.toString().length;
    const {isOnOverwolf} = useOverwolfInterop();
    const {isOnDarkMode} = useThemePreference();

    const isCompleted = current >= total;

    const getAccentColor = () => {
        if (colorOverride) {
            return colorOverride;
        }
        if (isOnDarkMode) {
            return "#7F56D9";
        }
        return "#7859E6";
    };

    return (
        <div className={classNames("circular-progress-indicator", {
            "glow": showGlow && isOnOverwolf,
            "completed": isCompleted,
            "has-long-text": strLength >= 4,
        })}
        style={{
            boxShadow: colorOverride && showGlow && isOnOverwolf ? `0 0 10px 3px ${getAccentColor()}` : undefined,
        }}
        >
            <svg viewBox="0 0 32 32">
                {/* Permanent Stroke Glow Filter for the GoVIP dual reward modal
                (filter does not apply on the Overwolf side)
                */}
                <defs>
                    <filter id="glow" x="-30%" y="-30%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                        <feFlood floodColor={getAccentColor()} result="floodColor"/>
                        <feComposite in="floodColor" in2="coloredBlur" operator="in"/>
                        <feMerge>
                            <feMergeNode/>
                            <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                    </filter>
                </defs>

                <circle className="background-circle" cx="16" cy="16" r="15"/>
                {percentage > 0 &&
                    <circle className="progress-circle" cx="16" cy="16" r="15" style={{
                        stroke: colorOverride,
                    }} strokeDasharray={`${percentage} 100`}/>
                }
            </svg>
            <div className="circular-progress-indicator-text"
                style={{
                    color: colorOverride && isCompleted ? colorOverride : undefined,
                }}
            >
                <span className={classNames("circular-progress-indicator-text_current", {
                    "purple-font": current > 0,
                    "smaller-font": current > 99,
                })}
                style={{
                    color: colorOverride && current > 0 ? colorOverride : undefined,
                }}
                >{formatToK(current)}</span>
                <span className={classNames("circular-progress-indicator-text_total", {
                    "smaller-font": total > 99,
                })}>
                    {" / "}{formatToK(total)}
                </span>
            </div>
        </div>
    );
}

export default CircularProgressIndicator;
